import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import Modal from 'components/_UI/Modal';
import { ArrowLeft, Mail, PlusSquare } from 'lucide-react';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import FaqForm from '../faq/FaqForm';
import FaqList from '../faq/FaqList';
import { useEvent } from 'context/events/Event';
import faqApi from 'api/requests/faq';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function FaqSection({ data, loading, onBack, toast, onSuccess }) {
    const { event } = useEvent();
    const [modalFaq, setModalFaq] = useState(false);
    const [deletingFaq, setDeletingFaq] = useState(false);

    function toggleModalAction(action) {
        if (action?.type === 'faq' || action?.type === 'editFaq') {
            setModalFaq(action);
            return;
        }
        if (action.type === 'deleteFaq') {
            setModalFaq(action);
            return;
        }
    }

    async function deleteFaq(id) {
        setDeletingFaq(true);
        try {
            await faqApi.delete(event?.slug, id);
            setModalFaq(false);
            toast.success('Pergunta excluída com sucesso!', { position: 'top-center' });
            onSuccess();
        } catch (error) {
        } finally {
            setDeletingFaq(false);
        }
    }

    function handleSuccess(item) {
        setModalFaq(false);
        if (item === 'createFaq') {
            toast.success('Pergunta criada com sucesso!', { position: 'top-center' });
        }
        if (item === 'editFaq') {
            toast.success('Pergunta editada com sucesso!', { position: 'top-center' });
        }
        onSuccess();
    }

    const count = data?.length || 0;

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={panelAccent(colors.secondaryPurple)} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <Mail size={20} color={panelAccent(colors.occasionalPurple)} />
                        </IconBadge>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>FAQ do evento</SectionTitle>
                                {count > 0 && <CountBadge>{count}</CountBadge>}
                            </TitleRow>
                            <SectionDescription>
                                Crie uma seção de Perguntas Frequentes para ajudar seus participantes.
                            </SectionDescription>
                        </HeaderText>
                    </HeaderLeft>

                    <ActionsRow>
                        <Button
                            variant='featured'
                            category='squired'
                            onClick={() => toggleModalAction({ type: 'faq' })}
                        >
                            <PlusSquare color={colors.neutralLight} size={16} />
                            <Text
                                color={colors.neutralLight}
                                paddingBottom={0}
                                marginLeft={'6px'}
                            >
                                Adicionar pergunta
                            </Text>
                        </Button>
                    </ActionsRow>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : count > 0 ? (
                    <ContentArea>
                        <FaqList
                            faq={data}
                            action={toggleModalAction}
                        />
                    </ContentArea>
                ) : (
                    <EmptyState>
                        <EmptyIcon>
                            <Mail size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhuma pergunta criada ainda.
                        </Text>
                        <EmptyHint>
                            Responda dúvidas comuns e facilite a experiência dos participantes.
                        </EmptyHint>
                    </EmptyState>
                )}
            </SectionCard>

            {modalFaq?.type === 'deleteFaq' && (
                <Modal isOpen={modalFaq?.type === 'deleteFaq'} onClose={() => setModalFaq(false)}>
                    <Text>Tem certeza que deseja excluir essa pergunta?</Text>
                    {deletingFaq ? (
                        <Loader />
                    ) : (
                        <ModalActions>
                            <Button onClick={() => setModalFaq(false)} variant='light'>Cancelar</Button>
                            <Button onClick={() => deleteFaq(modalFaq?.item?.id)}>Confirmar</Button>
                        </ModalActions>
                    )}
                </Modal>
            )}

            {(modalFaq?.type === 'faq' || modalFaq?.type === 'editFaq') && (
                <FaqForm
                    modalFaq={modalFaq}
                    edit={modalFaq?.item}
                    setModalFaq={setModalFaq}
                    onSuccess={handleSuccess}
                />
            )}
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    color: ${colors.neutralLight};
    background: ${panelAccent(colors.secondaryPurple)};
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.night}0A;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;

const ContentArea = styled.div`
    display: flex;
    flex-direction: column;
`;

const ModalActions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
